import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { differenceInCalendarDays, parseISO } from "date-fns";
import { CheckCircle2, CreditCard, ExternalLink, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, get, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAsync } from "@/lib/queries";
import { formatDate } from "@/lib/constants";
import { contactHref, money, planBullets, priceLabel, showPerMonth } from "@/lib/plans";
import { ErrorState, LoadingRows, PageHeader } from "@/components/States";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { TextTokens } from "@/components/TextTokens";
import { BillingHistory } from "@/components/BillingHistory";

const STATUS_TEXT = { active: "Active", trialing: "Active", past_due: "Payment overdue", canceled: "Cancelled", unpaid: "Unpaid", incomplete: "Payment incomplete" };

function planState(c) {
  if (c.suspended) return { label: "Suspended", tone: "rose", note: "This company has been suspended. Contact Warrant Whizz support." };
  if (c.complimentary) return { label: "Complimentary", tone: "emerald", note: "Your company has free access. No subscription needed." };
  if (STATUS_TEXT[c.subscription_status] && ["active", "trialing", "past_due"].includes(c.subscription_status)) {
    const end = c.current_period_end ? formatDate(c.current_period_end.slice(0, 10), "dd/MM/yyyy") : null;
    const left = c.current_period_end ? Math.max(0, differenceInCalendarDays(parseISO(c.current_period_end.slice(0, 10)), new Date())) : null;
    const inDays = left === 0 ? "today" : `in ${left} day${left === 1 ? "" : "s"}`;
    const note = !end ? null : c.cancel_at_period_end ? `Cancelled: your plan ends ${inDays} (${end}) and won't renew.`
      : c.subscription_status === "past_due" ? `Payment overdue since ${end}.` : `Renews automatically ${inDays} (${end}) unless cancelled.`;
    return { label: STATUS_TEXT[c.subscription_status], tone: c.subscription_status === "past_due" ? "amber" : "emerald", note, subscribed: true, daysLeft: left };
  }
  const days = c.trial_ends_at ? differenceInCalendarDays(parseISO(c.trial_ends_at), new Date()) : 0;
  return days > 0
    ? { label: "Free trial", tone: "blue", note: `${days} day${days === 1 ? "" : "s"} left in your free trial.` }
    : { label: c.subscription_status === "canceled" ? "Cancelled" : "Trial ended", tone: "amber", note: "Subscribe to continue using Warrant Whizz. Your data is safe." };
}

const TONES = { rose: "bg-rose-50 text-rose-700", emerald: "bg-emerald-50 text-emerald-700", amber: "bg-amber-50 text-amber-800", blue: "bg-blue-50 text-blue-800" };

function usePaymentReturn(onPaid) {
  const [params, setParams] = useSearchParams();
  const [checking, setChecking] = useState(false);
  const done = useRef(false);
  const sessionId = params.get("session_id");
  useEffect(() => {
    if (!sessionId || done.current) return undefined;
    done.current = true;
    let tries = 0;
    setChecking(true);
    const poll = async () => {
      try {
        const s = await get(`/billing/status/${sessionId}`);
        if (s.payment_status === "paid") {
          toast.success("Subscription active. Thank you!");
          setParams({}, { replace: true });
          setChecking(false);
          onPaid();
          return;
        }
      } catch {
        // retry below
      }
      if (++tries < 15) setTimeout(poll, 2000);
      else setChecking(false);
    };
    poll();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);
  return checking;
}

const usageText = (used, max) => (max ? `${used} of ${max}` : `${used} (unlimited)`);

const PlanCard = ({ p, current, action, busy, onPick }) => (
  <div className={`flex flex-col rounded-xl border bg-white p-5 shadow-sm ${current ? "border-blue-600 ring-1 ring-blue-600" : p.featured ? "border-amber-400 ring-1 ring-amber-400 shadow-md" : "border-slate-200"}`} data-testid={`billing-plan-${p.id}`}>
    <div className="flex items-start justify-between gap-2">
      <p className="font-display text-lg font-bold text-slate-900">{p.name}</p>
      <div className="flex flex-wrap justify-end gap-1">
        {p.featured && <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800" data-testid={`billing-plan-featured-${p.id}`}>Most popular</span>}
        {current && <span className="rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-800" data-testid={`billing-plan-current-${p.id}`}>Current</span>}
      </div>
    </div>
    <p className="font-display mt-1 text-2xl font-extrabold text-slate-900" data-testid={`billing-plan-price-${p.id}`}>{priceLabel(p)}{showPerMonth(p) && <span className="text-sm font-medium text-slate-500"> / month</span>}</p>
    {p.description && <p className="mt-1 text-sm text-slate-500">{p.description}</p>}
    <ul className="mt-3 flex-1 space-y-1.5 text-sm text-slate-700">
      {planBullets(p).map((t) => (
        <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" /> {t}</li>))}
    </ul>
    {p.display_only && !current && (
      <Button asChild variant="outline" className="mt-4" data-testid={`billing-plan-contact-${p.id}`}><a href={contactHref(p)}><Mail className="mr-1 h-4 w-4" /> Contact us</a></Button>)}
    {action && !current && !p.display_only && (
      <Button onClick={() => onPick(p)} disabled={!!busy} className="mt-4 bg-blue-700 hover:bg-blue-800" data-testid={`billing-plan-pick-${p.id}`}>
        {busy === p.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CreditCard className="mr-1 h-4 w-4" />} {action}</Button>)}
  </div>
);

export default function Billing() {
  const { profile, refreshProfile } = useAuth();
  const owner = profile?.company?.owner_user_id;
  const canBill = !owner || owner === profile?.id || !!profile?.support_view;
  const { data, loading, error, reload } = useAsync(() => (canBill ? get("/billing") : Promise.resolve(null)), [canBill]);
  const [busy, setBusy] = useState(null);
  const [switchTo, setSwitchTo] = useState(null);
  const checking = usePaymentReturn(() => { reload(); refreshProfile(); });

  const go = async (kind, planId) => {
    setBusy(planId || kind);
    try {
      const res = await post(kind === "subscribe" ? "/billing/checkout" : "/billing/portal", { origin_url: window.location.origin, plan_id: planId });
      window.location.href = res.checkout_url || res.url;
    } catch (e) {
      toast.error(errorMessage(e));
      setBusy(null);
    }
  };

  const [cancelling, setCancelling] = useState(false);
  const doSwitch = async () => {
    try {
      const res = await post("/billing/switch", { plan_id: switchTo.id, origin_url: window.location.origin });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
        return undefined;
      }
      toast.success(res.effective ? `${switchTo.name} starts on ${formatDate(res.effective.slice(0, 10))}` : `Switched to ${switchTo.name}`);
      reload();
      refreshProfile();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };

  const doCancel = async () => {
    try {
      await post("/billing/cancel");
      toast.success("Your plan won't renew. You keep access until the end of the paid month.");
      reload();
      refreshProfile();
    } catch (e) {
      toast.error(errorMessage(e));
      return false;
    }
    return undefined;
  };

  const c = data?.company;
  const s = c && planState(c);
  const canPick = c && !c.complimentary && !c.suspended;
  const action = s?.subscribed ? "Switch to this plan" : "Continue to secure checkout";
  const square = data?.provider === "square";
  const pick = (p) => (s.subscribed ? setSwitchTo(p) : go("subscribe", p.id));
  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader eyebrow={c?.name || profile?.company?.name || "Company"} title="Billing" description={canBill ? "Your Warrant Whizz plan, payments and text tokens." : "Buy text tokens for run texts."} />
      <TextTokens />
      {error && <ErrorState message={error} onRetry={reload} />}
      {loading && !data && <LoadingRows rows={2} />}
      {checking && <p className="flex items-center gap-2 rounded-lg bg-blue-50 p-3 text-sm text-blue-800" data-testid="billing-checking"><Loader2 className="h-4 w-4 animate-spin" /> Confirming your payment…</p>}
      {data && (
        <section className="max-w-xl space-y-5 rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="font-data text-[11px] uppercase tracking-[0.18em] text-blue-700" data-testid="billing-plan-name">{c.complimentary ? "Complimentary" : data.plan?.name || "Company plan"}</p>
              {!c.complimentary && data.price.amount != null && <p className="font-display mt-1 text-3xl font-extrabold text-slate-900" data-testid="billing-price">{money(data.price.amount)}<span className="text-base font-medium text-slate-500"> / month</span></p>}
            </div>
            <span className={`rounded-full px-3 py-1 text-xs font-semibold ${TONES[s.tone]}`} data-testid="billing-status">{s.label}</span>
          </div>
          {s.note && <p className="text-sm text-slate-700" data-testid="billing-note">{s.note}</p>}
          {c.subscription_status === "past_due" && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800" role="alert" data-testid="billing-payment-failed">Your last monthly payment failed. Please check the card saved with {square ? "Square" : "Stripe"} so your plan keeps running.</p>}
          {data.provider && (
            <p className="text-sm text-slate-600" data-testid="billing-provider">
              Paid via {square ? "Square" : "Stripe"}
              {data.pending_plan ? ` · changing to ${data.pending_plan} from then` : ""}{c.cancel_at_period_end ? " · won't renew" : ""}
            </p>
          )}
          <dl className="grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3 text-sm" data-testid="billing-usage">
            <div><dt className="text-xs text-slate-500">Locksmiths</dt><dd className="font-data font-semibold text-slate-900" data-testid="billing-usage-locksmiths">{usageText(data.usage.locksmiths, !c.complimentary && data.plan?.max_locksmiths)}</dd></div>
            <div><dt className="text-xs text-slate-500">Runs this month</dt><dd className="font-data font-semibold text-slate-900" data-testid="billing-usage-runs">{usageText(data.usage.runs_this_month, !c.complimentary && data.plan?.max_runs_per_month)}</dd></div>
          </dl>
          {canPick && (data.has_customer || (square && s.subscribed && !c.cancel_at_period_end)) && (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
              {data.has_customer && !square && <Button variant="outline" onClick={() => go("portal")} disabled={!!busy} data-testid="billing-manage-button">
                {busy === "portal" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <ExternalLink className="mr-1 h-4 w-4" />} Manage billing</Button>}
              {square && s.subscribed && !c.cancel_at_period_end && <Button variant="outline" onClick={() => setCancelling(true)} data-testid="billing-cancel-button">Cancel plan</Button>}
            </div>
          )}
        </section>
      )}
      {data && canPick && data.plans.length > 0 && (
        <section className="space-y-3" data-testid="billing-plans">
          <h2 className="font-display text-base font-bold text-slate-900 md:text-lg">Plans</h2>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {data.plans.map((p) => <PlanCard key={p.id} p={p} current={p.id === data.plan?.id && s.subscribed} action={action} busy={busy} onPick={pick} />)}
          </div>
          {!s.subscribed && <p className="text-xs text-slate-500">Pick a plan to subscribe. Prices are monthly and you can cancel any time.</p>}
        </section>
      )}
      <BillingHistory />
      <ConfirmDialog open={!!switchTo} onOpenChange={(o) => !o && setSwitchTo(null)} onConfirm={doSwitch} confirmLabel="Switch plan"
        title={`Switch to ${switchTo?.name}?`} description={square ? `${switchTo?.name} (${money(switchTo?.price)} a month) starts on your next billing date${c?.current_period_end ? `, ${formatDate(c.current_period_end.slice(0, 10))}` : ""}. There are no part-month charges or credits.`
          : data?.provider === "stripe" ? `You'll pay for ${switchTo?.name} (${money(switchTo?.price)} a month) on Square's secure checkout. Your Stripe subscription is cancelled once Square confirms, so you're never charged twice.`
          : `Your plan changes now. The new price of ${money(switchTo?.price)} a month applies from your next bill.`} />
      <ConfirmDialog open={cancelling} onOpenChange={setCancelling} onConfirm={doCancel} confirmLabel="Cancel plan" title="Cancel your plan?"
        description={`Your plan stops renewing. You keep full access until ${c?.current_period_end ? formatDate(c.current_period_end.slice(0, 10)) : "the end of the paid month"}.`} />
    </div>
  );
}
